import { readJpegPhotoMeta } from "./jpegGps";

export type GeoFix = {
  lat: number;
  lng: number;
  accuracyM?: number;
  source: "EXIF" | "LIVE";
  capturedAt: Date;
};

export type StampedPhoto = {
  file: File;
  previewUrl: string;
  lat: number;
  lng: number;
  accuracyM?: number;
  capturedAt: string;
  source: "EXIF" | "LIVE";
  stampText: string;
};

const loadImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read that photo"));
    };
    img.src = url;
  });

const formatStamp = (fix: GeoFix): { lines: string[]; stampText: string } => {
  const capturedAt = fix.capturedAt;
  const date = capturedAt.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const time = capturedAt.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
  const hemiLat = fix.lat >= 0 ? "N" : "S";
  const hemiLng = fix.lng >= 0 ? "E" : "W";
  const lat = `${Math.abs(fix.lat).toFixed(6)}° ${hemiLat}`;
  const lng = `${Math.abs(fix.lng).toFixed(6)}° ${hemiLng}`;
  const acc =
    Number.isFinite(fix.accuracyM) && (fix.accuracyM as number) > 0
      ? `±${Math.round(fix.accuracyM as number)} m`
      : "";
  const lines = [date, time, lat, lng, acc, "HastKalaPramanak"].filter(Boolean);
  return { lines, stampText: lines.join(" · ") };
};

const drawStamp = (img: HTMLImageElement, lines: string[]): HTMLCanvasElement => {
  const maxW = 1600;
  const scale = img.width > maxW ? maxW / img.width : 1;
  const width = Math.max(1, Math.round(img.width * scale));
  const height = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not stamp this photo");
  }
  ctx.drawImage(img, 0, 0, width, height);
  const barH = Math.max(96, Math.round(height * 0.2));
  const gradient = ctx.createLinearGradient(0, height - barH, 0, height);
  gradient.addColorStop(0, "rgba(0,0,0,0)");
  gradient.addColorStop(0.35, "rgba(0,0,0,0.45)");
  gradient.addColorStop(1, "rgba(0,0,0,0.82)");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, height - barH, width, barH);
  ctx.fillStyle = "#ffffff";
  ctx.textBaseline = "top";
  const titleSize = Math.max(16, Math.round(width * 0.028));
  const lineSize = Math.max(13, Math.round(width * 0.022));
  let y = height - barH + 14;
  ctx.font = `600 ${titleSize}px sans-serif`;
  ctx.fillText(lines[0] || "", 18, y);
  y += titleSize + 6;
  ctx.font = `500 ${lineSize}px sans-serif`;
  for (let i = 1; i < lines.length; i++) {
    ctx.fillText(lines[i], 18, y);
    y += lineSize + 4;
  }
  return canvas;
};

const canvasToJpeg = (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Could not save stamped photo"));
          return;
        }
        resolve(blob);
      },
      "image/jpeg",
      0.88
    );
  });

export const stampLoomPhoto = async (file: File, liveFix?: GeoFix | null): Promise<StampedPhoto> => {
  const meta = await readJpegPhotoMeta(file);
  let fix: GeoFix | null = null;
  if (meta.gps && Number.isFinite(meta.gps.lat) && Number.isFinite(meta.gps.lng)) {
    fix = {
      lat: meta.gps.lat,
      lng: meta.gps.lng,
      source: "EXIF",
      capturedAt: meta.capturedAt || new Date(),
    };
  } else if (liveFix && Number.isFinite(liveFix.lat) && Number.isFinite(liveFix.lng)) {
    fix = {
      ...liveFix,
      source: "LIVE",
      capturedAt: liveFix.capturedAt || meta.capturedAt || new Date(),
    };
  }
  if (!fix) {
    throw new Error("Turn on location, or choose a photo that already has GPS");
  }
  const img = await loadImage(file);
  const { lines, stampText } = formatStamp(fix);
  const canvas = drawStamp(img, lines);
  const blob = await canvasToJpeg(canvas);
  const stamped = new File([blob], "loom-geocam.jpg", { type: "image/jpeg" });
  return {
    file: stamped,
    previewUrl: URL.createObjectURL(blob),
    lat: Number(fix.lat.toFixed(6)),
    lng: Number(fix.lng.toFixed(6)),
    accuracyM: fix.accuracyM,
    capturedAt: fix.capturedAt.toISOString(),
    source: fix.source,
    stampText,
  };
};
